import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { ImageUpload } from './FormComponents';
import { formatBRLDisplay, parseBRLInput } from '../utils/dashboardHelpers';

export const GROUP_TYPES = [
  ['OFFER_UNIT', 'Complementos'],
  ['SPECIFICATION', 'Escolha única'],
  ['INGREDIENTS', 'Ingredientes'],
  ['CUTLERY', 'Talheres'],
  ['SIZE', 'Tamanho da pizza'],
  ['CRUST', 'Massa da pizza'],
  ['EDGE', 'Borda da pizza'],
  ['TOPPING', 'Sabores da pizza'],
];

export const createPizzaGroups = () => ([
  { client_key: 'pizza-size', titulo: 'Tamanho', tipo: 'SIZE', qtd_minima: 1, qtd_maxima: 1, ativo: true, opcoes: [] },
  { client_key: 'pizza-crust', titulo: 'Massa', tipo: 'CRUST', qtd_minima: 1, qtd_maxima: 1, ativo: true, opcoes: [] },
  { client_key: 'pizza-edge', titulo: 'Borda', tipo: 'EDGE', qtd_minima: 0, qtd_maxima: 1, ativo: true, opcoes: [] },
  { client_key: 'pizza-topping', titulo: 'Sabores', tipo: 'TOPPING', qtd_minima: 1, qtd_maxima: 4, ativo: true, opcoes: [] },
]);

const makeKey = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const OptionGroupsManager = ({ groups = [], onChange, itemType = 'DEFAULT' }) => {
  const updateGroup = (index, patch) => onChange(groups.map((group, position) => (
    position === index ? { ...group, ...patch } : group
  )));
  const removeGroup = (index) => onChange(groups.filter((_, position) => position !== index));
  const addGroup = () => onChange([
    ...groups,
    {
      client_key: makeKey('group'),
      titulo: '',
      tipo: itemType === 'PIZZA' ? 'TOPPING' : 'OFFER_UNIT',
      qtd_minima: 0,
      qtd_maxima: 1,
      ativo: true,
      opcoes: [],
    },
  ]);
  const updateOption = (groupIndex, optionIndex, patch) => updateGroup(groupIndex, {
    opcoes: (groups[groupIndex].opcoes || []).map((option, position) => (
      position === optionIndex ? { ...option, ...patch } : option
    )),
  });
  const addOption = (groupIndex) => updateGroup(groupIndex, {
    opcoes: [
      ...(groups[groupIndex].opcoes || []),
      { client_key: makeKey('option'), nome: '', descricao: '', preco: 0, ativo: true, fractions: [] },
    ],
  });
  const removeOption = (groupIndex, optionIndex) => updateGroup(groupIndex, {
    opcoes: (groups[groupIndex].opcoes || []).filter((_, position) => position !== optionIndex),
  });

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={800}>Grupos e escolhas</Typography>
        <Typography variant="body2" color="text.secondary">
          Defina o que o cliente pode ou precisa escolher ao adicionar este item.
        </Typography>
      </Box>
      {groups.map((group, groupIndex) => (
        <Paper key={group.id || group.client_key || groupIndex} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography fontWeight={800}>Grupo {groupIndex + 1}</Typography>
            <Button color="error" size="small" startIcon={<DeleteOutline />} onClick={() => removeGroup(groupIndex)}>
              Remover
            </Button>
          </Stack>
          <Stack spacing={1.5} mt={1.5}>
            <TextField
              label="Nome do grupo"
              value={group.titulo || ''}
              onChange={(event) => updateGroup(groupIndex, { titulo: event.target.value })}
              fullWidth required
            />
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={group.tipo || 'OFFER_UNIT'}
                onChange={(event) => updateGroup(groupIndex, { tipo: event.target.value })}
              >
                {GROUP_TYPES
                  .filter(([type]) => itemType === 'PIZZA' || !['SIZE', 'CRUST', 'EDGE', 'TOPPING'].includes(type))
                  .map(([type, label]) => <MenuItem key={type} value={type}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1.5}>
              <TextField
                label="Mínimo"
                type="number"
                value={group.qtd_minima ?? 0}
                onChange={(event) => updateGroup(groupIndex, { qtd_minima: Math.max(0, Number(event.target.value) || 0) })}
                inputProps={{ min: 0 }} fullWidth
              />
              <TextField
                label="Máximo"
                type="number"
                value={group.qtd_maxima ?? 1}
                onChange={(event) => updateGroup(groupIndex, { qtd_maxima: Math.max(0, Number(event.target.value) || 0) })}
                inputProps={{ min: 0 }} fullWidth
              />
            </Stack>
            <FormControlLabel
              control={<Switch checked={group.ativo !== false} onChange={(event) => updateGroup(groupIndex, { ativo: event.target.checked })} />}
              label="Grupo ativo"
            />
            <Divider />
            {(group.opcoes || []).map((option, optionIndex) => (
              <Box key={option.id || option.client_key || optionIndex} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={800}>Opção {optionIndex + 1}</Typography>
                  <Button color="error" size="small" onClick={() => removeOption(groupIndex, optionIndex)}>Remover</Button>
                </Stack>
                <Stack spacing={1.25}>
                  <ImageUpload
                    image={option.foto_url || ''}
                    label="Imagem da opção"
                    recommendedSize="800 × 800 px"
                    outputWidth={800}
                    outputHeight={800}
                    onImageAdjusted={(image) => updateOption(groupIndex, optionIndex, { foto_url: image })}
                  />
                  <TextField
                    label="Nome"
                    value={option.nome || ''}
                    onChange={(event) => updateOption(groupIndex, optionIndex, { nome: event.target.value })}
                    fullWidth required
                  />
                  <TextField
                    label="Descrição"
                    value={option.descricao || ''}
                    onChange={(event) => updateOption(groupIndex, optionIndex, { descricao: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Preço adicional"
                    value={option.preco_input ?? formatBRLDisplay(option.preco || 0)}
                    onChange={(event) => {
                      const parsed = parseBRLInput(event.target.value);
                      updateOption(groupIndex, optionIndex, { preco: parsed.numeric, preco_input: parsed.display });
                    }}
                    fullWidth
                  />
                  {group.tipo === 'SIZE' && (
                    <Box>
                      <Typography variant="body2" fontWeight={700}>Quantidade de sabores permitida</Typography>
                      <Stack direction="row" flexWrap="wrap" useFlexGap>
                        {[1, 2, 3, 4].map((fraction) => (
                          <FormControlLabel
                            key={fraction}
                            control={(
                              <Checkbox
                                checked={(option.fractions || []).map(Number).includes(fraction)}
                                onChange={(event) => {
                                  const current = new Set((option.fractions || []).map(Number));
                                  if (event.target.checked) current.add(fraction); else current.delete(fraction);
                                  updateOption(groupIndex, optionIndex, { fractions: [...current].sort() });
                                }}
                              />
                            )}
                            label={fraction}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                  <FormControlLabel
                    control={<Switch checked={option.ativo !== false} onChange={(event) => updateOption(groupIndex, optionIndex, { ativo: event.target.checked })} />}
                    label="Opção ativa"
                  />
                </Stack>
              </Box>
            ))}
            <Button variant="outlined" startIcon={<Add />} onClick={() => addOption(groupIndex)}>
              Adicionar opção
            </Button>
          </Stack>
        </Paper>
      ))}
      <Button variant="outlined" startIcon={<Add />} onClick={addGroup}>Adicionar grupo</Button>
    </Stack>
  );
};

export default OptionGroupsManager;
